import { invalidateTypedQueries, useApiClient } from '@/hooks/useApiClient'
import { formatters } from '@/utils/intl/dateTimeFormatters'
import { GraviButton, Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { DatePicker, Form, Modal, TimePicker, message } from 'antd'
import type { Dayjs } from 'dayjs'
import { useState } from 'react'
import { useSchedulePageFilters } from '../../Context/Filters/SchedulePageFilterContext'
import { DriversSelect } from '../../Forms/Inputs/DriversSelect'

type BulkAddShiftsFormType = {
  dates?: Dayjs[]
  start_time: Dayjs | null
  end_time: Dayjs | null
  drivers: string[] | null
}

export function BulkAddShiftsModal(props: Parameters<typeof GraviButton>[0]) {
  const api = useApiClient()
  const { dateRange } = useSchedulePageFilters()
  const [isOpen, setIsOpen] = useState(false)
  const [form] = Form.useForm<BulkAddShiftsFormType>()
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = api.useMutation('post', '/driver/update_driver_schedules', {
    onSuccess: () => setIsOpen(false),
    onError: (err) => err?.detail && typeof err.detail === 'string' && message.error(err.detail),
    onSettled: () => invalidateTypedQueries(queryClient, 'post', '/driver/schedule/overview'),
  })

  return (
    <>
      <Modal
        open={isOpen}
        title='Bulk Edit Shift Times'
        okText='Save'
        destroyOnClose
        okButtonProps={{ loading: isPending }}
        onCancel={() => setIsOpen(false)}
        onClose={() => setIsOpen(false)}
        onOk={() => {
          form.submit()
        }}
      >
        <Form
          clearOnDestroy
          onValuesChange={(_, values) => {
            if (!values.start_time && !values.end_time) return

            let startTime = values.start_time
            let endTime = values.end_time

            if (startTime && endTime && endTime.isBefore(startTime)) {
              const tempTime = startTime
              startTime = endTime
              endTime = tempTime

              form.setFieldsValue({
                start_time: startTime,
                end_time: endTime,
              })
            }
          }}
          onFinish={async (values) => {
            const transformedPayload = {
              dates: values?.dates?.map((d) => formatters.WITHOUT_TIME(d)) || [],
              driver_ids: values?.drivers || [],
              start_time: values?.start_time?.format('YYYY-MM-DDTHH:mm:ss') || undefined,
              end_time: values?.end_time?.format('YYYY-MM-DDTHH:mm:ss') || undefined,
            }

            await mutateAsync({ body: transformedPayload })
          }}
          form={form}
          layout='vertical'
        >
          <Vertical gap={8} className='mt-4'>
            <DriversSelect name='drivers' style={{ marginBottom: 8 }} />
            <Form.Item
              name='dates'
              label='Dates'
              style={{ marginBottom: 8 }}
              rules={[{ required: true, message: 'Please select at least one day' }]}
            >
              <DatePicker
                format={'MMM DD, YYYY'}
                maxTagCount={5}
                multiple
                disabledDate={(d) => {
                  return d.valueOf() < dateRange.startDate.valueOf() || d.valueOf() > dateRange.endDate.valueOf()
                }}
              />
            </Form.Item>
            <Horizontal flex={1} width='100%' gap={16} style={{ flexWrap: 'nowrap' }}>
              <Form.Item
                name='start_time'
                label='Start Time'
                style={{ flex: 1 }}
                dependencies={['end_time']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const endTime = getFieldValue('end_time')
                      if (!value && !endTime) {
                        return Promise.reject(new Error('Either start time or end time is required'))
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <TimePicker
                  minuteStep={5}
                  allowClear={true}
                  style={{ minWidth: '100%' }}
                  format='h:mm A'
                  needConfirm={false}
                />
              </Form.Item>
              <Form.Item name='end_time' label='End Time' style={{ flex: 1 }}>
                <TimePicker
                  minuteStep={5}
                  allowClear={true}
                  style={{ minWidth: '100%' }}
                  format='h:mm A'
                  needConfirm={false}
                />
              </Form.Item>
            </Horizontal>
          </Vertical>
        </Form>
      </Modal>
      <GraviButton
        {...props}
        onClick={(e) => {
          setIsOpen(true)
          props?.onClick?.(e)
        }}
      />
    </>
  )
}
